/**
 * 🛰️ RailCabinet — 車站／路線／標籤獨立；捷運／輕軌／火車／高鐵；在建／規劃
 */
import { SovereignCabinet } from '../../core/cabinet-base.js';
import {
    getElevationLabel,
    getAccessIcons,
    buildExitLabelHtml,
    geometryToLineParts,
    resolveRailLineColor,
    classifyRailPhase,
    railFeatureVisible,
    buildRailPhaseContext,
    formatExitDisplayName,
    clusterRailStations,
    buildRailStationMarkerHtml,
    buildStationMemberLines,
    summarizeStationElevations
} from '../../utils/rail-utils.js?v=17.0.825';
import { addElevationOutlinedLine } from '../../utils/elevation-line-style.js?v=17.0.825';
import { assignLabelShifts } from '../../utils/bus-map-label-declutter.js?v=17.0.653';
import { readMapFocusQuery } from '../../utils/map-focus-from-query.js?v=17.0.653';
import { searchGisPlaces } from '../../utils/gis-place-search.js';
import { isCompactMapClient } from '../../utils/map-client-profile.js';
import { googleMapsNavButtonHtml } from '../../utils/google-maps-nav.js';
import {
    mapErrorReportLinkHtml,
    bindMapErrorReportLinks
} from '../../utils/map-error-report.js?v=17.0.826';

function coordsHitBounds(coords, bounds) {
    if (!Array.isArray(coords) || !coords.length) return false;
    if (typeof coords[0] === 'number') {
        const lat = Number(coords[1]);
        const lng = Number(coords[0]);
        return Number.isFinite(lat) && Number.isFinite(lng) && bounds.contains([lat, lng]);
    }
    return coords.some((child) => coordsHitBounds(child, bounds));
}

function featureHitsBounds(feature, bounds) {
    if (!feature?.geometry || !bounds) return true;
    return coordsHitBounds(feature.geometry.coordinates, bounds);
}

function esc(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

class RailCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.activeLayers = [];
        this.exitLayerGroup = null;
        this._payload = null;
        this._loading = null;
        this.loadAssets('/js/cabinets/rail');
    }

    onMount() {
        this.bindToggles();
        this.bindSearch();
        this.syncLabelEnables();
        this.revealEditLink();
        this.applyFocusFromQuery();
    }

    afterAssetsMounted() {
        if (this.map) this.applyView();
    }

    onMapReady(map) {
        this.map = map;
        this.exitLayerGroup = L.layerGroup();
        this._onMapLabels = () => this.scheduleLabelDeclutter();
        this.map.on('moveend', this._onMapLabels);
        this.map.on('zoomend', this._onMapLabels);
        this._onCompactClip = () => {
            if (!isCompactMapClient()) return;
            const f = this.readFilters?.();
            if (!f || (!f.stations && !f.routes && !f.exits)) return;
            this.scheduleCompactClip();
        };
        this.map.on('moveend', this._onCompactClip);
        this.map.on('zoomend', this._onCompactClip);
        this.ensureGlobalLabelStyles();
        this.applyFocusFromQuery();
        this.applyView();
    }

    ensureGlobalLabelStyles() {
        if (document.querySelector('link[data-rail-map-css]')) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/css/rail-map-overlays.css?v=17.0.655';
        link.setAttribute('data-rail-map-css', '17.0.655');
        document.head.appendChild(link);
    }

    scheduleCompactClip() {
        clearTimeout(this._compactClipTimer);
        this._compactClipTimer = setTimeout(() => this.applyView(), 220);
    }

    paddedMapBounds() {
        if (!this.map) return null;
        return this.map.getBounds().pad(0.12);
    }

    scheduleLabelDeclutter() {
        clearTimeout(this._labelDeclutterTimer);
        this._labelDeclutterTimer = setTimeout(() => {
            this.applyLabelDeclutter();
            setTimeout(() => this.applyLabelDeclutter(), 160);
        }, 80);
    }

    collectLabelTooltipLayers() {
        const layers = [];
        const walk = (layer) => {
            if (!layer) return;
            if (typeof layer.eachLayer === 'function' && !layer.getTooltip?.()) {
                layer.eachLayer(walk);
                return;
            }
            const tip = layer.getTooltip?.();
            if (tip?.options?.permanent) layers.push(layer);
            else if (typeof layer.eachLayer === 'function') layer.eachLayer(walk);
        };
        this.activeLayers.forEach(walk);
        return layers;
    }

    applyLabelDeclutter() {
        const layers = this.collectLabelTooltipLayers();
        const items = [];
        for (const layer of layers) {
            const el = layer.getTooltip?.()?.getElement?.();
            if (!el) continue;
            el.classList.remove('rail-map-label-hidden');
            el.style.marginLeft = '';
            el.style.marginTop = '';
            const r = el.getBoundingClientRect();
            items.push({ el, left: r.left, top: r.top, width: r.width, height: r.height });
        }
        const assigned = assignLabelShifts(
            items.map(({ left, top, width, height }) => ({ left, top, width, height })),
            {
                candidates: [
                    { dx: 0, dy: 0 },
                    { dx: 0, dy: -22 },
                    { dx: 0, dy: -44 },
                    { dx: 0, dy: 22 },
                    { dx: 0, dy: 44 },
                    { dx: 32, dy: 0 },
                    { dx: -32, dy: 0 },
                    { dx: 32, dy: -20 },
                    { dx: -32, dy: -20 },
                    { dx: 32, dy: 20 },
                    { dx: -32, dy: 20 },
                    { dx: 0, dy: -66 },
                    { dx: 48, dy: -36 },
                    { dx: -48, dy: -36 }
                ]
            }
        );
        assigned.forEach((a, i) => {
            const el = items[i].el;
            if (a.hidden) {
                el.classList.add('rail-map-label-hidden');
                el.style.marginLeft = '';
                el.style.marginTop = '';
                return;
            }
            el.classList.remove('rail-map-label-hidden');
            el.style.marginLeft = a.dx ? `${a.dx}px` : '';
            el.style.marginTop = a.dy ? `${a.dy}px` : '';
        });
    }

    setStatus(text) {
        const el = this.shadowRoot?.getElementById('status-text');
        if (el) el.innerText = text;
    }

    revealEditLink() {
        const role = window.__SOVEREIGN_MAP_BOOT__?.user?.role;
        if (role !== 'SUPER_ADMIN' && role !== 'ADMIN' && role !== 'SOVEREIGN_ADMIN') return;
        const el = this.el('rail-edit-link');
        if (el) el.hidden = false;
    }

    applyFocusFromQuery() {
        if (this._focusApplied) return false;
        if (!readMapFocusQuery()) return false;
        const exits = this.el('toggleExits');
        const construction = this.el('toggleConstruction');
        if (!exits || !construction) return false;
        exits.checked = true;
        construction.checked = true;
        const labels = this.el('toggleExitLabels');
        if (labels) {
            labels.disabled = false;
            labels.checked = true;
        }
        this._focusApplied = true;
        return true;
    }

    el(id) {
        return this.shadowRoot?.getElementById(id);
    }

    checked(id) {
        return !!this.el(id)?.checked;
    }

    readFilters() {
        return {
            stations: this.checked('toggleStations'),
            routes: this.checked('toggleRoutes'),
            exits: this.checked('toggleExits'),
            stationLabels: this.checked('toggleStationLabels'),
            routeLabels: this.checked('toggleRouteLabels'),
            exitLabels: this.checked('toggleExitLabels'),
            construction: this.checked('toggleConstruction'),
            planned: this.checked('togglePlanned'),
            modes: {
                mrt: this.checked('modeMrt'),
                lrt: this.checked('modeLrt'),
                train: this.checked('modeTrain'),
                hsr: this.checked('modeHsr')
            }
        };
    }

    syncLabelEnables() {
        const stationOn = this.checked('toggleStations');
        const routeOn = this.checked('toggleRoutes');
        const exitOn = this.checked('toggleExits');
        const sl = this.el('toggleStationLabels');
        const rl = this.el('toggleRouteLabels');
        const el = this.el('toggleExitLabels');
        if (sl) {
            sl.disabled = !stationOn;
            if (!stationOn) sl.checked = false;
        }
        if (rl) {
            rl.disabled = !routeOn;
            if (!routeOn) rl.checked = false;
        }
        if (el) {
            el.disabled = !exitOn;
            if (!exitOn) el.checked = false;
        }
    }

    bindSearch() {
        const input = this.el('railSearch');
        if (!input) return;
        let timer = null;
        input.addEventListener('input', () => {
            clearTimeout(timer);
            timer = setTimeout(() => this.runSearch(input.value), 140);
        });
    }

    async runSearch(raw) {
        const box = this.el('railSearchHits');
        if (!box) return;
        const q = String(raw || '').trim();
        if (!q) {
            box.hidden = true;
            box.innerHTML = '';
            return;
        }
        const { hits } = await searchGisPlaces(q, { kinds: 'rail_station,rail_route', limit: 16 });
        if (!hits.length) {
            box.hidden = false;
            box.innerHTML = '<div class="gis-search-empty">沒有符合的車站／路線</div>';
            return;
        }
        box.hidden = false;
        box.innerHTML = hits.map((h, i) => `
            <button type="button" class="gis-search-hit" data-i="${i}">
                <b>${esc(h.name)}</b>
                <div style="font-size:11px;color:#94a3b8">${esc(h.kind_zh || '')} · ${esc(h.sub || '')}</div>
            </button>`).join('');
        box.querySelectorAll('.gis-search-hit').forEach((btn) => {
            btn.addEventListener('click', () => {
                const hit = hits[Number(btn.getAttribute('data-i'))];
                if (hit) this.selectSearchHit(hit);
            });
        });
    }

    async selectSearchHit(hit) {
        const box = this.el('railSearchHits');
        if (box) box.hidden = true;
        if (hit.kind === 'rail_station') {
            const st = this.el('toggleStations');
            if (st) st.checked = true;
        } else {
            const rt = this.el('toggleRoutes');
            if (rt) rt.checked = true;
        }
        this.syncLabelEnables();
        await this.applyView();
        if (Number.isFinite(Number(hit.lat)) && Number.isFinite(Number(hit.lon))) {
            this.map?.setView([Number(hit.lat), Number(hit.lon)], 15);
        }
        this.setStatus(`已定位 ${hit.name || ''}`);
    }

    bindToggles() {
        const ids = [
            'toggleStations', 'toggleRoutes', 'toggleExits',
            'toggleStationLabels', 'toggleRouteLabels', 'toggleExitLabels',
            'modeMrt', 'modeLrt', 'modeTrain', 'modeHsr',
            'toggleConstruction', 'togglePlanned'
        ];
        for (const id of ids) {
            this.el(id)?.addEventListener('change', () => {
                this.syncLabelEnables();
                this.applyView();
            });
        }
    }

    resolveLineColor(p) {
        return resolveRailLineColor(p);
    }

    buildTransferPopupHtml(p) {
        const traId =
            p.h_tra_station_id ||
            (p.ref && String(p.ref).padStart(4, '0')) ||
            null;
        if (!traId || !this._transferIndex?.by_tra_station_id) return '';
        const entry = this._transferIndex.by_tra_station_id[traId];
        if (!entry?.members?.length) return '';
        const lines = [];
        for (const m of entry.members) {
            if (m.Service && m.Service !== 'TRA') {
                const zh = m.StationName?.Zh_tw || m.StationName?.zh_tw || m.Service;
                lines.push(`<li>${esc(m.Service)} · ${esc(zh)}</li>`);
            }
            for (const block of m.Transfers || []) {
                for (const t of block.RailTransfers || []) {
                    const zh = t.StationName?.Zh_tw || t.StationName?.zh_tw || t.Service || '鐵路';
                    lines.push(`<li>${esc(zh)}</li>`);
                }
                for (const t of block.BusTransfers || []) {
                    const zh = t.StationName?.Zh_tw || t.StopName?.Zh_tw || '公車';
                    lines.push(`<li>公車 · ${esc(zh)}</li>`);
                }
                for (const t of block.ParkingTransfers || []) {
                    const zh = t.CarParkName?.Zh_tw || t.Description || '停車';
                    lines.push(`<li>停車 · ${esc(zh)}</li>`);
                }
                for (const t of block.TaxiTransfers || []) {
                    const zh = t.TaxiStopName?.Zh_tw || t.Description || '計程車';
                    lines.push(`<li>計程車 · ${esc(zh)}</li>`);
                }
                for (const t of block.BikeTransfers || []) {
                    const zh = t.StationName?.Zh_tw || 'YouBike';
                    lines.push(`<li>自行車 · ${esc(zh)}</li>`);
                }
            }
        }
        const uniq = [...new Set(lines)].slice(0, 10);
        if (!uniq.length) return '';
        return `<div style="margin-top:8px;font-size:11px;"><b>轉乘／銜接</b><ul style="margin:4px 0;padding-left:18px;">${uniq.join('')}</ul></div>`;
    }

    async ensurePayload() {
        if (this._loading) return this._loading;
        const filters = this.readFilters?.() || {};
        const have = this._payload;
        const needRoutes = !have?.routesData;
        const needStations = !!(filters.stations && !have?._gotStations);
        const needExits = !!(filters.exits && !have?._gotExits);
        const needPlanned = !!(filters.planned && !have?._gotPlanned);
        if (have && !needRoutes && !needStations && !needExits && !needPlanned) {
            return have;
        }
        this.setStatus('載入中...');
        this._loading = (async () => {
            const next = have || {
                routesData: { type: 'FeatureCollection', features: [] },
                stationsData: { type: 'FeatureCollection', features: [] },
                exitsData: { type: 'FeatureCollection', features: [] },
                _gotStations: false,
                _gotExits: false,
                _gotPlanned: false
            };
            const jobs = [];
            if (needRoutes) jobs.push(['routes', fetch('/api/live/rail/routes', { credentials: 'include' })]);
            if (needStations) {
                jobs.push(['stations', fetch('/api/live/rail/stations', { credentials: 'include' })]);
                jobs.push(['transfer', fetch('/api/live/rail/transfer-index', { credentials: 'include' })]);
            }
            if (needExits) jobs.push(['exits', fetch('/api/live/rail/exits', { credentials: 'include' })]);
            if (needPlanned) {
                jobs.push(['planned', fetch('/api/live/rail/routes?include=planned', { credentials: 'include' })]);
            }
            const settled = await Promise.all(jobs.map(async ([key, p]) => [key, await p]));
            const byKey = Object.fromEntries(settled);
            const failAuth = (res) => {
                const err = new Error(res.status === 401
                    ? '請先登入後再載入軌道圖資'
                    : '軌道圖資載入失敗');
                err.status = res.status;
                throw err;
            };
            if (byKey.routes) {
                if (!byKey.routes.ok) failAuth(byKey.routes);
                next.routesData = await byKey.routes.json();
            }
            if (byKey.stations) {
                if (!byKey.stations.ok) failAuth(byKey.stations);
                next.stationsData = await byKey.stations.json();
                next._gotStations = true;
            }
            if (byKey.transfer?.ok) this._transferIndex = await byKey.transfer.json();
            if (byKey.exits) {
                if (!byKey.exits.ok) failAuth(byKey.exits);
                next.exitsData = await byKey.exits.json();
                next._gotExits = true;
            }
            if (byKey.planned) {
                if (!byKey.planned.ok) failAuth(byKey.planned);
                const extra = await byKey.planned.json();
                next.routesData = {
                    ...next.routesData,
                    features: [...(next.routesData.features || []), ...(extra.features || [])]
                };
                next._gotPlanned = true;
            }
            this._payload = next;
            return next;
        })();
        try {
            return await this._loading;
        } finally {
            this._loading = null;
        }
    }

    async applyView() {
        if (!this.map) return;
        this.applyFocusFromQuery();
        const filters = this.readFilters();
        if (!filters.stations && !filters.routes && !filters.exits) {
            this.clearLayers();
            this.setStatus('就緒');
            return;
        }
        try {
            const payload = await this.ensurePayload();
            this.renderLayers(payload, filters);
        } catch (err) {
            console.error('[RailCabinet]', err);
            this.setStatus(err.message === '請先登入後再載入軌道圖資' ? '❌ 請先登入' : '❌ 載入失敗');
        }
    }

    renderLayers({ routesData, stationsData, exitsData }, filters) {
        this.clearLayers();
        const ctx = buildRailPhaseContext(stationsData.features || [], routesData.features || []);
        let routeN = 0;
        let stationN = 0;

        if (filters.routes) {
            const routeGroup = L.layerGroup();
            const compact = isCompactMapClient();
            const zoom = this.map?.getZoom() ?? 8;
            const clipBounds = compact ? this.paddedMapBounds() : null;
            const skipOutline = compact && zoom < 13;
            for (const feature of routesData.features || []) {
                const p = feature.properties || {};
                if (!railFeatureVisible(p, filters, ctx)) continue;
                if (clipBounds && !featureHitsBounds(feature, clipBounds)) continue;
                const parts = geometryToLineParts(feature.geometry);
                if (!parts.length) continue;
                const mainColor = this.resolveLineColor(p);
                const elev = p._std_elevation || p.elevation || 'unknown';
                const name = p.name || p._std_name || '未命名';
                const sys = p.sys || p._std_sys || '';
                const phase = classifyRailPhase(p);
                const elevZh = getElevationLabel(elev);
                const dashArray = phase === 'operating' ? undefined : '8 6';
                const featureGroup = L.layerGroup();
                let tooltipHost = null;
                for (const latLngs of parts) {
                    const { group, main } = addElevationOutlinedLine(
                        this.map, latLngs, mainColor, elev, { addTo: false, dashArray, skipOutline }
                    );
                    featureGroup.addLayer(group);
                    if (!tooltipHost) tooltipHost = main;
                }
                if (tooltipHost) {
                    const phaseZh = phase === 'construction' ? '在建' : phase === 'planned' ? '規劃' : '營運';
                    const reportHtml = mapErrorReportLinkHtml({
                        layer: 'rail_route',
                        feature_std_id: p._std_id || p.id || p.ref || '',
                        sys,
                        name,
                        current_elevation: elev
                    });
                    tooltipHost.bindPopup(
                        `<div style="font-size:11px;"><b>${esc(name)}</b><br/>${esc(sys)} · ${phaseZh} · 外框:${elevZh}<br/>${reportHtml}</div>`
                    );
                    tooltipHost.on('popupopen', (ev) => {
                        bindMapErrorReportLinks(ev.popup.getElement(), {
                            map: this.map,
                            appVersion: this.appVersion
                        });
                    });
                    if (filters.routeLabels) {
                        tooltipHost.bindTooltip(
                            `<div style="font-size:11px;"><b>${esc(name)}</b><br/>${esc(sys)} · ${phaseZh}</div>`,
                            { sticky: true, permanent: true, direction: 'center' }
                        );
                    }
                }
                routeGroup.addLayer(featureGroup);
                routeN += 1;
            }
            routeGroup.addTo(this.map);
            this.activeLayers.push(routeGroup);
        }

        if (filters.stations) {
            const compact = isCompactMapClient();
            const clipBounds = compact ? this.paddedMapBounds() : null;
            const visible = (stationsData.features || []).filter((f) => {
                if (!railFeatureVisible(f.properties || {}, filters, ctx)) return false;
                if (clipBounds && !featureHitsBounds(f, clipBounds)) return false;
                return true;
            });
            const clusters = clusterRailStations(visible);
            stationN = clusters.length;
            const stationsLayer = L.layerGroup();
            for (const cluster of clusters) {
                const { html, size } = buildRailStationMarkerHtml(cluster.rings);
                const marker = L.marker([cluster.lat, cluster.lng], {
                    icon: L.divIcon({
                        className: 'rail-station-marker',
                        html,
                        iconSize: [size, size],
                        iconAnchor: [size / 2, size / 2]
                    }),
                    keyboard: false
                });
                const elevSummary = summarizeStationElevations(cluster.members);
                const lineRows = buildStationMemberLines(cluster.members).map((row) => esc(row));
                const elevNote =
                    elevSummary.mode === 'multi'
                        ? `<div style="font-size:11px;margin-top:4px;color:#555;">高程依線／系統分列（轉乘站可能不同）</div>`
                        : '';
                let transferBlock = '';
                for (const f of cluster.members) {
                    transferBlock = this.buildTransferPopupHtml(f.properties || {});
                    if (transferBlock) break;
                }
                const primary = cluster.members[0]?.properties || {};
                const elevForReport =
                    elevSummary.mode === 'single'
                        ? elevSummary.elev
                        : primary._std_elevation || primary.elevation || 'unknown';
                const reportHtml = mapErrorReportLinkHtml({
                    layer: 'rail_station',
                    feature_std_id: primary._std_id || '',
                    sys: primary._std_sys || primary.sys || '',
                    name: cluster.name,
                    current_elevation: elevForReport
                });
                const stationNav = googleMapsNavButtonHtml(cluster.lat, cluster.lng);
                marker.bindPopup(`
                    <div style="padding:8px;min-width:180px;">
                        <div style="font-size:14px;font-weight:700;">${esc(cluster.name)}</div>
                        ${stationNav}
                        <div style="font-size:11px;margin-top:4px;">${lineRows.join('<br/>')}</div>
                        ${elevNote}
                        ${transferBlock}
                        <div style="margin-top:8px;">${reportHtml}</div>
                    </div>
                `);
                marker.on('popupopen', (ev) => {
                    bindMapErrorReportLinks(ev.popup.getElement(), {
                        map: this.map,
                        appVersion: this.appVersion
                    });
                });
                if (filters.stationLabels) {
                    marker.bindTooltip(esc(cluster.name), {
                        permanent: true,
                        direction: 'right',
                        className: 'rail-station-label',
                        offset: [Math.round(size / 2) + 2, 0]
                    });
                    marker.openTooltip();
                }
                stationsLayer.addLayer(marker);
            }
            stationsLayer.addTo(this.map);
            this.activeLayers.push(stationsLayer);
        }

        let exitN = 0;
        const compact = isCompactMapClient();
        const zoom = this.map?.getZoom() ?? 8;
        const showExits = filters.exits && (!compact || zoom >= 15);
        if (filters.exits && this.exitLayerGroup && !showExits) {
            this.exitLayerGroup.clearLayers();
        }
        if (showExits && this.exitLayerGroup) {
            this.exitLayerGroup.clearLayers();
            const clipBounds = compact ? this.paddedMapBounds() : null;
            for (const feature of exitsData.features || []) {
                const p = feature.properties || {};
                if (!feature.geometry?.coordinates) continue;
                if (!railFeatureVisible(p, filters, ctx)) continue;
                if (clipBounds && !featureHitsBounds(feature, clipBounds)) continue;
                const [lng, lat] = feature.geometry.coordinates;
                const access = p._std_access || '';
                const elev = p._std_elevation || p.elevation || 'unknown';
                const label = formatExitDisplayName(p, ctx.stationNameById);
                const marker = L.circleMarker([lat, lng], {
                    radius: 4,
                    fillColor: '#fff',
                    color: '#333',
                    weight: 1,
                    fillOpacity: 0.95
                });
                marker.bindTooltip(buildExitLabelHtml(label, access, elev), {
                    permanent: filters.exitLabels,
                    direction: 'top',
                    className: 'rail-exit-tooltip'
                });
                if (filters.exitLabels) marker.openTooltip();
                const reportHtml = mapErrorReportLinkHtml({
                    layer: 'rail_exit',
                    feature_std_id: p._std_id || p.id || '',
                    sys: p._std_sys || p.sys || '',
                    name: label,
                    current_elevation: elev
                });
                const exitNav = googleMapsNavButtonHtml(lat, lng);
                marker.bindPopup(`
                    <div style="padding:8px;">
                        <b>${esc(label)}</b>
                        ${exitNav}
                        <div style="margin-top:4px;">${getElevationLabel(elev)}<br/>
                        ${getAccessIcons(access, elev)}</div>
                        <div style="margin-top:8px;">${reportHtml}</div>
                    </div>
                `);
                marker.on('popupopen', (ev) => {
                    bindMapErrorReportLinks(ev.popup.getElement(), {
                        map: this.map,
                        appVersion: this.appVersion
                    });
                });
                this.exitLayerGroup.addLayer(marker);
                exitN += 1;
            }
            this.exitLayerGroup.addTo(this.map);
            this.activeLayers.push(this.exitLayerGroup);
        }

        let status = `✅ ${routeN} 路線 / ${stationN} 站 / ${exitN} 出口`;
        if (compact && filters.exits && zoom < 15) {
            status += ` · 出口請放大至 15 級`;
        }
        this.setStatus(status);
        this.scheduleLabelDeclutter();
    }

    clearLayers() {
        if (!this.map) return;
        this.activeLayers.forEach((layer) => this.map.removeLayer(layer));
        this.activeLayers = [];
        if (this.exitLayerGroup) this.exitLayerGroup.clearLayers();
    }
}

if (!customElements.get('rail-cabinet')) {
    customElements.define('rail-cabinet', RailCabinet);
}

function dock() {
    if (window.appCore) {
        const instance = document.createElement('rail-cabinet');
        const container = document.getElementById('socket-rail');
        if (container) {
            container.innerHTML = '';
            container.appendChild(instance);
            window.appCore.registerCabinet('rail', instance);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
